import React, { useState, useEffect } from 'react';
import { Modal, Button, Row, Col, Card, Spinner, Badge } from 'react-bootstrap';
import { Check } from 'lucide-react';
import { billingService } from '../../services/billingService';
import { initializePaddle } from '@paddle/paddle-js';

const PlanSelectionModal = ({ show, onHide, token, onSuccess }) => {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [paddle, setPaddle] = useState(null);

    useEffect(() => {
        // Initialize Paddle
        const init = async () => {
            try {
                // Should be in env: process.env.REACT_APP_PADDLE_CLIENT_TOKEN
                const paddleInstance = await initializePaddle({
                    token: 'test_client_token', // Placeholder: User needs to replace this
                    environment: 'sandbox'
                });
                setPaddle(paddleInstance);
            } catch (e) {
                console.error("Failed to init paddle", e);
            }
        };
        init();
    }, []);

    useEffect(() => {
        if (show) {
            loadPlans();
        }
    }, [show]);

    const loadPlans = async () => {
        try {
            const data = await billingService.getPlans(token);
            setPlans(data.filter(p => p.price_per_user_cents > 0));
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handleSubscribe = async (plan) => {
        // Direct Upgrade Implementation (Bypassing Paddle)
        setProcessing(true);
        try {
            await billingService.directUpgrade(token, plan.id);
            onSuccess();
            onHide();
        } catch (e) {
            console.error(e);
            alert("Upgrade failed. Please try again.");
        } finally {
            setProcessing(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title className="fw-bold">Upgrade your Plan</Modal.Title>
            </Modal.Header>
            <Modal.Body className="bg-light">
                {loading ? (
                    <div className="text-center py-5"><Spinner animation="border" /></div>
                ) : (
                    <Row className="g-4 justify-content-center">
                        {plans.map(plan => (
                            <Col md={6} key={plan.id}>
                                <Card className="h-100 border-0 shadow-sm text-center overflow-hidden">
                                    <div className={`py-2 ${plan.name === 'Unlimited' ? 'bg-primary text-white' : 'bg-white text-dark border-bottom'}`}>
                                        <h5 className="mb-0 fw-bold">{plan.name}</h5>
                                    </div>
                                    <Card.Body className="d-flex flex-column p-4">
                                        <div className="mb-4">
                                            <span className="display-4 fw-bold">
                                                {(plan.price_per_user_cents / 100).toFixed(2)}
                                            </span>
                                            <span className="text-muted text-uppercase fw-bold" style={{ fontSize: '0.8rem' }}>
                                                &nbsp;{plan.currency || 'EUR'} / {plan.billing_interval || 'year'}
                                            </span>
                                        </div>

                                        <ul className="list-unstyled text-start mb-4 flex-grow-1 mx-auto">
                                            <li className="mb-2"><Check size={18} className="text-success me-2" /> Calendar Sync</li>
                                            <li className="mb-2"><Check size={18} className="text-success me-2" /> KI Koch</li>
                                            {plan.name === 'Unlimited' && (
                                                <li className="mb-2"><Check size={18} className="text-success me-2" /> AI Chef Unlimited</li>
                                            )}
                                        </ul>

                                        <Button
                                            variant={plan.name === 'Unlimited' ? 'primary' : 'outline-primary'}
                                            size="lg"
                                            onClick={() => handleSubscribe(plan)}
                                            disabled={processing}
                                        >
                                            {processing ? <Spinner size="sm" /> : "Checkout with Paddle"}
                                        </Button>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                        {plans.length === 0 && <div className="text-center">No upgrade plans available.</div>}
                    </Row>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default PlanSelectionModal;
